import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {
  ConversationStatusesEnum,
  makeFindConversationActionContext,
} from '../../../../../helpers/models/conversationHelpers';
import {parseEmailMessageBody} from '../../../../../helpers/models/messageHelpers';
import {ReplyTypesEnum} from '../../../../../helpers/models/recipientHelpers';
import {ResolveDispatchProps} from '../../../../../helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
  wrapInShallowSelector,
} from '../../../../../helpers/reselectHelpers';
import {deleteMessageTask} from '../../../../../services/tasks/deleteMessageTask';
import {InterfaceEventsEnum, ReplyLocationsEnum, trackInterfaceEvent} from '../../../../../services/tracking';
import {AppState} from '../../../../../store/appState';
import {assignToTeammateAction} from '../../../../../store/data/conversations/assigneeConversationsActions';
import {
  makeGetAreAllShared,
  makeGetAreAllSubscribed,
  makeGetAssigneeForAll,
  makeGetReminderForAll,
} from '../../../../../store/data/conversations/conversationsMultiSelectors';
import {
  makeGetAreAllInTeamContext,
  makeGetConversationContextNamespaces,
} from '../../../../../store/data/conversations/conversationsSelectors';
import {setConversationsStatusAction} from '../../../../../store/data/conversations/statusConversationsActions';
import {
  subscribeToConversationsAction,
  unsubscribeFromConversationsAction,
} from '../../../../../store/data/conversations/subscribeConversationsActions';
import {updateConversationSubjectAction} from '../../../../../store/data/conversations/updateConversationSubjectAction';
import {toggleFlagAction} from '../../../../../store/data/localPreferences/localPreferencesActions';
import {cloneMessage, replyToMessageAction} from '../../../../../store/data/messages/draftMessagesActions';
import {
  getDefaultReplyType,
  getShouldReplyInNewConversation,
  getTimeFormat,
} from '../../../../../store/data/preferences/preferencesSelectors';
import {getUserStarredTagId} from '../../../../../store/data/tags/tagsSelectors';
import {getFindTeammate} from '../../../../../store/data/teammates/lazyTeammatesSelectors';
import {getIsUserNewUser} from '../../../../../store/data/teammates/teammatesSelectors';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {InboxesVisualConversationSelection} from '../../../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {
  makeGetKnownSelectedConversations,
  makeGetOnlySelectedConversation,
} from '../../../../../store/panels/cells/selectors/cellsPanelSelectionSelectors';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {removePanelAction} from '../../../../../store/panels/panelsActions';
import {CellsPanelId, ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {makeGetIsBulkActionRequired} from '../../../../../store/panels/viewer/conversations/viewerPanelConversationsSelectors';
import {navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {NewConversationTopbar} from './newConversationTopbar';

/*
 * Props.
 */

interface ConversationTopbarContainerProps {
  panelId: ViewerPanelId;
  cellsPanelId: CellsPanelId | undefined;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversationSelection: InboxesVisualConversationSelection;
}

/*
 * Selectors.
 */

function getCellsPanelId(_state: unknown, props: ConversationTopbarContainerProps) {
  return props.cellsPanelId;
}

function getCellsContext(_state: unknown, props: ConversationTopbarContainerProps) {
  return props.cellsContext;
}

function getInboxesParameters(_state: unknown, props: ConversationTopbarContainerProps) {
  return props.inboxesParameters;
}

function getConversationSelection(_state: unknown, props: ConversationTopbarContainerProps) {
  return props.conversationSelection;
}

/** Extract the teammate ID from the cells context in the props. */
function getTeammateId(_state: AppState, props: ConversationTopbarContainerProps) {
  return props.cellsContext.teammateId;
}

function makeGetTeammate() {
  return createSelector([getTeammateId, getFindTeammate], (teammateId, findTeammate) => {
    const teammate = findTeammate(teammateId, 'ConversationTopbarContainer makeGetTeammate');
    if (!teammate) {
      throw new Error('A teammate could not be found for the current context.');
    }

    return teammate;
  });
}

const getShouldShowSubscribe = createSelector(getUserStarredTagId, (starredTagId) => Boolean(starredTagId));

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationTopbarContainerProps>()(() => {
  const getCurrentConversations = makeGetKnownSelectedConversations(
    getConversationSelection,
    getCellsPanelId,
  );
  const getOnlyConversation = makeGetOnlySelectedConversation(getConversationSelection);
  const getTeammate = makeGetTeammate();

  return {
    teammate: getTeammate,
    onlyConversation: getOnlyConversation,
    conversations: getCurrentConversations,
    contextNamespaces: wrapInShallowSelector(
      makeGetConversationContextNamespaces(getCellsContext, getCurrentConversations),
    ),
    assigneeForAll: makeGetAssigneeForAll(getCurrentConversations),
    reminderForAll: makeGetReminderForAll(getCellsContext, getCurrentConversations),
    areAllShared: makeGetAreAllShared(getCurrentConversations),
    areAllSubscribed: makeGetAreAllSubscribed(getCellsContext, getCurrentConversations),
    areAllInTeamContext: makeGetAreAllInTeamContext(getCellsContext, getCurrentConversations),
    shouldShowSubscribe: getShouldShowSubscribe,
    replyType: getDefaultReplyType,
    shouldReplyInNewConversation: getShouldReplyInNewConversation,
    timeFormat: getTimeFormat,
    shouldShowNewUserTooltip: getIsUserNewUser,
    isBulkActionRequired: makeGetIsBulkActionRequired(getInboxesParameters, getConversationSelection),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<ConversationTopbarContainerProps>()((props) => ({
  replyToMessageAction,
  cloneMessage,
  setConversationsStatusAction,
  assignToTeammateAction,
  toggleFlagAction,
  deleteMessageTask,
  navigateToLinkAction,
  removePanelAction,
  subscribeToConversationsAction,
  unsubscribeFromConversationsAction,
  updateConversationSubjectAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: ConversationTopbarContainerProps,
) {
  const {panelId, cellsContext} = ownProps;
  const {onlyConversation, conversations, shouldReplyInNewConversation, teammate} = stateProps;
  // TODO: update this to be selection to support bulk actions
  const conversationIds = conversations.map((c) => c.id);

  const replyBase = (replyType: ReplyTypesEnum, location: ReplyLocationsEnum, shouldPopout?: boolean) => {
    if (!onlyConversation) {
      return;
    }

    const {id, lastMessage} = onlyConversation;

    dispatchProps.replyToMessageAction({
      panelId,
      conversationId: id,
      replyToUid: (lastMessage && lastMessage.uid) || '',
      replyType,
      location,
      shouldPopout,
      shouldCreateConversation: shouldReplyInNewConversation,
    });
  };

  const onReplyClick = (location: ReplyLocationsEnum, shouldPopout?: boolean) =>
    replyBase(ReplyTypesEnum.SINGLE, location, shouldPopout);

  const onReplyAllClick = (location: ReplyLocationsEnum, shouldPopout?: boolean) =>
    replyBase(ReplyTypesEnum.ALL, location, shouldPopout);

  const onReplyIntroClick = (location: ReplyLocationsEnum, shouldPopout?: boolean) =>
    replyBase(ReplyTypesEnum.INTRO, location, shouldPopout);

  const onForwardClick = (location: ReplyLocationsEnum, shouldPopout?: boolean) =>
    replyBase(ReplyTypesEnum.FORWARD, location, shouldPopout);

  const onCloneClick = (location: ReplyLocationsEnum) => {
    if (!onlyConversation || !onlyConversation.lastMessage) {
      return;
    }

    dispatchProps.cloneMessage(
      {
        panelId,
        conversationId: onlyConversation.id,
        originalMessageUid: onlyConversation.lastMessage.uid,
        location,
      },
      parseEmailMessageBody,
    );
  };

  const onDestroyClick = () => {
    dispatchProps.setConversationsStatusAction(
      cellsContext,
      conversationIds,
      ConversationStatusesEnum.DESTROYED,
      makeFindConversationActionContext(cellsContext, conversations),
      {preventUndo: true},
    );
  };

  const onDeleteMessageClick = (messageUid: string) => {
    dispatchProps.deleteMessageTask(messageUid);
  };

  const onToggleAssignToSelfClick = () => {
    // If some of the conversations are already assigned to current user then unassign.
    if (stateProps.assigneeForAll && stateProps.assigneeForAll.id === teammate.id) {
      return dispatchProps.assignToTeammateAction(conversationIds, undefined, {cellsContext});
    }

    // Otherwise, assign to current user.
    return dispatchProps.assignToTeammateAction(conversationIds, teammate.id, {cellsContext});
  };

  const onPopinClick = () => {
    if (!onlyConversation) {
      return;
    }

    dispatchProps.removePanelAction(panelId);
    dispatchProps.navigateToLinkAction(onlyConversation.link);
  };

  const onSubscribeClick = () => {
    trackInterfaceEvent(InterfaceEventsEnum.FOLLOW_CONVERSATION, {
      action: 'follow',
      teammate_id: teammate.id,
    });
    dispatchProps.subscribeToConversationsAction(cellsContext, conversationIds);
  };

  const onUnsubscribeClick = () => {
    trackInterfaceEvent(InterfaceEventsEnum.FOLLOW_CONVERSATION, {
      action: 'unfollow',
      teammate_id: teammate.id,
    });
    dispatchProps.unsubscribeFromConversationsAction(cellsContext, conversationIds);
  };

  const onSubjectChange = (subject: string) => {
    const ids = conversations.map((c) => c.id);
    dispatchProps.updateConversationSubjectAction(ids, subject);
  };

  return {
    ...ownProps,
    ...stateProps,
    onReplyClick,
    onReplyAllClick,
    onReplyIntroClick,
    onForwardClick,
    onCloneClick,
    onDestroyClick,
    onDeleteMessageClick,
    onToggleAssignToSelfClick,
    onPopinClick,
    onSubscribeClick,
    onUnsubscribeClick,
    onSubjectChange,
  };
}

// TODO: Delete this and all dependencies.
// export const ConversationTopbarContainer = connect(
//   mapStateToProps,
//   mapDispatchToProps,
//   mergeProps,
// )(ConversationTopbar);

// TODO: Rename this to ConversationTopbarContainer
export const NewConversationTopbarContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(NewConversationTopbar);
